import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileDown } from "lucide-react";
import logger from "@/lib/logger";
import { formatDateTimeIST } from "@/lib/formatUtils";

function getActionBadgeClass(action) {
  if (action?.includes("delete")) return "border-red-300 text-red-600";
  if (action?.includes("edit")) return "border-amber-300 text-amber-600";
  return "border-emerald-300 text-emerald-600";
}

export default function AuditTrailTab() {
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilter, setAuditFilter] = useState({ performed_by: "", collection: "", date_from: "", date_to: "" });

  const fetchAuditLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (auditFilter.performed_by) params.set("performed_by", auditFilter.performed_by);
      if (auditFilter.collection) params.set("collection", auditFilter.collection);
      if (auditFilter.date_from) params.set("date_from", auditFilter.date_from);
      if (auditFilter.date_to) params.set("date_to", auditFilter.date_to);
      const { data } = await api.get(`/admin/audit-trail?${params.toString()}`);
      setAuditLogs(data.entries || []);
    } catch (err) { logger.error("Failed to fetch audit logs:", err); }
  }, [auditFilter]);

  useEffect(() => { fetchAuditLogs(); }, [fetchAuditLogs]);

  const handleDownloadAudit = async () => {
    try {
      const params = new URLSearchParams();
      if (auditFilter.performed_by) params.set("performed_by", auditFilter.performed_by);
      if (auditFilter.collection) params.set("collection", auditFilter.collection);
      if (auditFilter.date_from) params.set("date_from", auditFilter.date_from);
      if (auditFilter.date_to) params.set("date_to", auditFilter.date_to);
      const res = await api.get(`/admin/audit-trail/download?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "audit_trail.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Audit log downloaded");
    } catch (err) { logger.error("Download failed:", err); toast.error("Download failed"); }
  };

  return (
    <Card className="p-4 sm:p-5 mt-4" data-testid="audit-trail-section">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
        <h3 className="font-bold text-base" style={{ fontFamily: "Chivo" }}>Audit Trail</h3>
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleDownloadAudit} data-testid="download-audit-btn">
          <FileDown size={14} className="mr-1" /> Download CSV
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <Input placeholder="User email" value={auditFilter.performed_by} onChange={e => setAuditFilter(p => ({ ...p, performed_by: e.target.value }))} className="h-9 text-xs" data-testid="audit-filter-user" />
        <select value={auditFilter.collection} onChange={e => setAuditFilter(p => ({ ...p, collection: e.target.value }))} className="h-9 border rounded px-2 text-xs bg-white" data-testid="audit-filter-collection">
          <option value="">All Collections</option>
          {["visitors","employee_movements","rgp_entries","nrgp_entries","jobwork_entries","dispatches","vehicle_movements","key_register","courier_register","interplant_transfers"].map(c => (
            <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
          ))}
        </select>
        <Input type="date" value={auditFilter.date_from} onChange={e => setAuditFilter(p => ({ ...p, date_from: e.target.value }))} className="h-9 text-xs" data-testid="audit-filter-from" />
        <Button size="sm" className="h-9 bg-[#1B3C87]" onClick={fetchAuditLogs} data-testid="audit-filter-apply">Apply</Button>
      </div>
      <div className="overflow-x-auto border rounded">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] uppercase tracking-wider font-bold px-3">Timestamp</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold px-3">User</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold px-3">Action</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold px-3">Collection</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-bold px-3">Entry ID</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {auditLogs.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-400">No audit entries found</TableCell></TableRow>
            )}
            {auditLogs.map((log, i) => (
              <TableRow key={log.id || `audit-${log.timestamp}-${i}`} className="hover:bg-blue-50/50">
                <TableCell className="text-xs px-3 text-gray-500">{log.timestamp ? formatDateTimeIST(log.timestamp) : "—"}</TableCell>
                <TableCell className="text-xs px-3">{log.performed_by || "—"}</TableCell>
                <TableCell className="text-xs px-3">
                  <Badge variant="outline" className={`text-[10px] ${getActionBadgeClass(log.action)}`}>
                    {log.action || "—"}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs px-3">{(log.collection || "").replace(/_/g, " ")}</TableCell>
                <TableCell className="text-xs px-3 font-mono text-gray-400">{(log.document_id || "").slice(-8)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-gray-400 mt-2">{auditLogs.length} entries shown (max 100)</p>
    </Card>
  );
}
